import { type FormEvent, useEffect, useState } from 'react';
import { i18n, wordCount } from '@wms/shared';
import { api, ApiError } from '../lib/api.js';
import { Dialog } from './Dialog.js';

const { t } = i18n;

interface SkuAttr {
  key: string;
  value: string;
}

export interface SkuEditInitial {
  code: string;
  nameLt: string;
  nameEn: string | null;
  attrs: SkuAttr[];
  slaOverrideWorkdays: number | null;
  lowWaterQty: number | null;
  notes: string | null;
  locationRow: string | null;
  locationShelf: string | null;
  locationPosition: string | null;
}

interface PartnerOption {
  id: string;
  code: string;
  name: string;
}

export interface SkuPartnerTransferResult {
  action: 'copy' | 'move';
  skuId: string;
  partnerId: string;
}

export function EditSkuDialog({
  partnerId,
  skuId,
  initial,
  onClose,
  onSaved,
  onTransferred,
  canTransfer = true,
  canCopy = true,
}: {
  partnerId: string;
  skuId: string;
  initial: SkuEditInitial;
  onClose: () => void;
  onSaved: () => void;
  onTransferred: (result: SkuPartnerTransferResult) => void;
  canTransfer?: boolean;
  canCopy?: boolean;
}) {
  const [code, setCode] = useState(initial.code);
  const [nameLt, setNameLt] = useState(initial.nameLt);
  const [nameEn, setNameEn] = useState(initial.nameEn ?? '');
  const [attrs, setAttrs] = useState<SkuAttr[]>(initial.attrs);
  const [slaOverride, setSlaOverride] = useState(
    initial.slaOverrideWorkdays === null ? '' : String(initial.slaOverrideWorkdays),
  );
  const [lowWater, setLowWater] = useState(
    initial.lowWaterQty === null ? '' : String(initial.lowWaterQty),
  );
  const [notes, setNotes] = useState(initial.notes ?? '');
  const [locationRow, setLocationRow] = useState(initial.locationRow ?? '');
  const [locationShelf, setLocationShelf] = useState(initial.locationShelf ?? '');
  const [locationPosition, setLocationPosition] = useState(initial.locationPosition ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [partners, setPartners] = useState<PartnerOption[]>([]);
  const [destinationPartnerId, setDestinationPartnerId] = useState('');
  const [transferReason, setTransferReason] = useState('');
  const [transferBusy, setTransferBusy] = useState(false);
  const [transferError, setTransferError] = useState<string | null>(null);

  useEffect(() => {
    if (!canTransfer) return;
    api<PartnerOption[]>('/partners/options')
      .then((options) => setPartners(options.filter((partner) => partner.id !== partnerId)))
      .catch(() => setPartners([]));
  }, [partnerId, canTransfer]);

  function setAttrField(i: number, field: keyof SkuAttr, value: string) {
    setAttrs((d) => d.map((a, j) => (j === i ? { ...a, [field]: value } : a)));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const filteredAttrs = attrs.filter((a) => a.key.trim() && a.value.trim());
    setBusy(true);
    try {
      await api(`/partners/${partnerId}/skus/${skuId}/metadata`, {
        method: 'PATCH',
        body: {
          code: code.trim(),
          nameLt,
          nameEn: nameEn.trim() ? nameEn : undefined,
          attrs: filteredAttrs,
          slaOverrideWorkdays: slaOverride === '' ? null : Number(slaOverride),
          lowWaterQty: lowWater === '' ? null : Number(lowWater),
          notes: notes.trim() ? notes : undefined,
          locationRow: locationRow.trim() || null,
          locationShelf: locationShelf.trim() || null,
          locationPosition: locationPosition.trim() || null,
        },
      });
      onSaved();
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'sku_code_taken'
          ? t('sku.code_taken')
          : t('common.error'),
      );
    } finally {
      setBusy(false);
    }
  }

  async function copyOrMove(action: 'copy' | 'move') {
    setTransferError(null);
    if (!destinationPartnerId || wordCount(transferReason) < 2) {
      setTransferError(t('sku.transfer.invalid'));
      return;
    }
    setTransferBusy(true);
    try {
      const result = await api<SkuPartnerTransferResult>(
        `/partners/${partnerId}/skus/${skuId}/partner-transfer`,
        {
          method: 'POST',
          body: { destinationPartnerId, action, reason: transferReason },
        },
      );
      onTransferred(result);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : '';
      const key =
        code === 'sku_destination_code_taken'
          ? 'sku.transfer.code_taken'
          : code === 'sku_destination_components_missing'
            ? 'sku.transfer.components_missing'
            : code === 'sku_transfer_in_use'
              ? 'sku.transfer.in_use'
              : 'common.error';
      setTransferError(t(key));
    } finally {
      setTransferBusy(false);
    }
  }

  return (
    <Dialog title={t('sku.edit.title')} onClose={onClose}>
      <form
        onSubmit={(e) => void onSubmit(e)}
        className="space-y-3 text-sm max-h-[70vh] overflow-y-auto pr-1"
      >
        <label className="block space-y-1">
          <span className="font-medium">{t('sku.col.code')}</span>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
            maxLength={64}
            className="w-full rounded border border-border bg-background px-2 py-1.5 font-mono"
          />
        </label>
        <label className="block space-y-1">
          <span className="font-medium">{t('sku.col.name_lt')}</span>
          <input
            value={nameLt}
            onChange={(e) => setNameLt(e.target.value)}
            required
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
        </label>
        <label className="block space-y-1">
          <span className="font-medium">{t('sku.col.name_en')}</span>
          <input
            value={nameEn}
            onChange={(e) => setNameEn(e.target.value)}
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
        </label>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="font-medium">{t('sku.edit.attrs')}</span>
            <button
              type="button"
              onClick={() => setAttrs((d) => [...d, { key: '', value: '' }])}
              className="rounded border border-border px-2 py-1 text-xs"
            >
              +
            </button>
          </div>
          {attrs.length === 0 ? (
            <div className="text-xs text-muted-foreground">{t('sku.edit.attrs_empty')}</div>
          ) : null}
          {attrs.map((a, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                placeholder={t('sku.edit.attr_key')}
                value={a.key}
                onChange={(e) => setAttrField(i, 'key', e.target.value)}
                className="flex-1 rounded border border-border bg-background px-2 py-1"
              />
              <input
                placeholder={t('sku.edit.attr_value')}
                value={a.value}
                onChange={(e) => setAttrField(i, 'value', e.target.value)}
                className="flex-1 rounded border border-border bg-background px-2 py-1"
              />
              <button
                type="button"
                onClick={() => setAttrs((d) => d.filter((_, j) => j !== i))}
                className="rounded border border-border px-2 py-1 text-xs"
              >
                ×
              </button>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <label className="block space-y-1">
            <span className="font-medium">{t('sku.col.sla_override')}</span>
            <input
              type="number"
              min={0}
              max={365}
              value={slaOverride}
              onChange={(e) => setSlaOverride(e.target.value)}
              placeholder={t('sku.edit.sla_inherit_hint')}
              className="w-full rounded border border-border bg-background px-2 py-1.5 tabular-nums"
            />
          </label>
          <label className="block space-y-1">
            <span className="font-medium">{t('sku.col.low_water')}</span>
            <input
              type="number"
              min={0}
              value={lowWater}
              onChange={(e) => setLowWater(e.target.value)}
              className="w-full rounded border border-border bg-background px-2 py-1.5 tabular-nums"
            />
          </label>
        </div>

        <div className="space-y-1">
          <span className="font-medium">{t('location.label')}</span>
          <div className="grid grid-cols-3 gap-2">
            <input
              value={locationRow}
              onChange={(e) => setLocationRow(e.target.value)}
              maxLength={8}
              placeholder={t('location.row')}
              className="rounded border border-border bg-background px-2 py-1.5 text-center font-mono"
            />
            <input
              value={locationShelf}
              onChange={(e) => setLocationShelf(e.target.value)}
              maxLength={8}
              placeholder={t('location.shelf')}
              className="rounded border border-border bg-background px-2 py-1.5 text-center font-mono"
            />
            <input
              value={locationPosition}
              onChange={(e) => setLocationPosition(e.target.value)}
              maxLength={8}
              placeholder={t('location.position')}
              className="rounded border border-border bg-background px-2 py-1.5 text-center font-mono"
            />
          </div>
          <p className="text-xs text-muted-foreground">{t('location.hint')}</p>
        </div>

        <label className="block space-y-1">
          <span className="font-medium">{t('sku.edit.notes')}</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={2000}
            rows={3}
            placeholder={t('sku.description.placeholder')}
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
        </label>

        {canTransfer && (
          <div className="space-y-2 border-t border-border pt-3">
            <div className="font-medium">{t('sku.transfer.title')}</div>
            <p className="text-xs text-muted-foreground">{t('sku.transfer.help')}</p>
            <label className="block space-y-1">
              <span className="font-medium">{t('sku.transfer.destination')}</span>
              <select
                value={destinationPartnerId}
                onChange={(e) => setDestinationPartnerId(e.target.value)}
                className="w-full rounded border border-border bg-background px-2 py-1.5"
              >
                <option value="">{t('sku.transfer.choose_partner')}</option>
                {partners.map((partner) => (
                  <option key={partner.id} value={partner.id}>
                    {partner.code} — {partner.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="block space-y-1">
              <span className="font-medium">{t('sku.transfer.reason')}</span>
              <textarea
                value={transferReason}
                onChange={(e) => setTransferReason(e.target.value)}
                rows={2}
                placeholder={t('sku.transfer.reason_hint')}
                className="w-full rounded border border-border bg-background px-2 py-1.5"
              />
            </label>
            {transferError ? <div className="text-red-600">{transferError}</div> : null}
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => void copyOrMove('copy')}
                disabled={
                  !canCopy || transferBusy || !destinationPartnerId || wordCount(transferReason) < 2
                }
                className="rounded border border-border px-3 py-1.5 disabled:opacity-50"
              >
                {t('sku.transfer.copy')}
              </button>
              <button
                type="button"
                onClick={() => void copyOrMove('move')}
                disabled={transferBusy || !destinationPartnerId || wordCount(transferReason) < 2}
                className="rounded border border-red-300 px-3 py-1.5 text-red-700 disabled:opacity-50"
              >
                {t('sku.transfer.move')}
              </button>
            </div>
          </div>
        )}

        {error ? <div className="text-red-600">{error}</div> : null}

        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border px-3 py-1.5"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={busy || !code.trim() || !nameLt.trim()}
            className="rounded bg-primary text-primary-foreground px-3 py-1.5 disabled:opacity-50"
          >
            {busy ? t('common.loading') : t('common.save')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
